/** Tenant-private AI Search workspace shared by the customer and approved-partner surfaces. */
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@qm/ui';
import { bff, bffJson } from '../../lib/bff';
import './auto-rag.css';

type Status = { scope: string; available: boolean; index: string; documents: {status:string;count:number}[] };
type DocumentRow = {id:string;title:string;category:string;status:string;created_at:string;version:number};
type SearchHit = {id?:string;text:string;score?:number;key?:string;title?:string;source_id?:string};
const CATEGORIES = ['onboarding','branding','website','crm','deliverables','reports','knowledge','proposals','support'];
const wording = {
 en: {
   eyebrow:'QUIXO MARKETING · PRIVATE KNOWLEDGE',title:'Your AI knowledge',
   subtitle:'Find answers in the approved information your team shares. Every customer and partner workspace is isolated.',
   connected:'Connected',unavailable:'Not configured',enable:'Enable private search',refresh:'Refresh',
   searchTitle:'Search your knowledge',searchHint:'Search for approved briefs, decisions, handover notes and other business information.',
   searchPlaceholder:'What was agreed for our website launch?',searchButton:'Find answers',
   searching:'Searching…',noResults:'No relevant passages were found. Try another question or add approved information.',
   passage:'Knowledge source',relevance:'Relevance',
   addTitle:'Add approved information',addHint:'Only information approved for your company knowledge base is indexed.',
   name:'Document name',category:'Type of information',details:'Paste approved information',
   consent:'I approve adding this information to our private knowledge base.',
   privacy:'This text contains no customer personal details, credentials, medical or other health data.',
   save:'Save and index',documents:'Knowledge sources',documentHint:'You can remove documents at any time. Indexing may take a few minutes.',
   empty:'Your knowledge base has no documents yet.',remove:'Remove',retry:'Retry indexing',
   privacyNote:'Only authorized users within your account can search this data.',
   error:'Could not complete that request. Please try again.',statusProcessing:'Indexing',statusQueued:'Queued',statusFailed:'Needs attention',
 },
 ar: {
   eyebrow:'كويكسو للتسويق · معرفة خاصة',title:'معرفة الذكاء الاصطناعي',
   subtitle:'ابحث في المعلومات المعتمدة التي يشاركها فريقك. لكل عميل وشريك مساحة معزولة.',
   connected:'متصل',unavailable:'غير مهيأ',enable:'تفعيل البحث الخاص',refresh:'تحديث',
   searchTitle:'ابحث في معرفتك',searchHint:'ابحث في الملخصات والقرارات والمستندات المعتمدة.',
   searchPlaceholder:'ما الذي اتفقنا عليه لإطلاق الموقع؟',searchButton:'ابحث',
   searching:'جارٍ البحث…',noResults:'لم نعثر على مقاطع ذات صلة. جرّب سؤالاً آخر أو أضف معلومات معتمدة.',
   passage:'المصدر',relevance:'الصلة',
   addTitle:'أضف معلومات معتمدة',addHint:'تتم فهرسة المعلومات التي وافقت على إضافتها إلى قاعدة المعرفة فقط.',
   name:'عنوان المستند',category:'نوع المعلومات',details:'الصق المعلومات المعتمدة',
   consent:'أوافق على إضافة هذه المعلومات إلى قاعدة المعرفة الخاصة بنا.',
   privacy:'هذا النص لا يحتوي على بيانات شخصية أو كلمات مرور أو بيانات طبية أو صحية.',
   save:'احفظ وافهرس',documents:'مصادر المعرفة',documentHint:'يمكنك حذف المستندات في أي وقت. قد تستغرق الفهرسة دقائق.',
   empty:'لا توجد مستندات في قاعدة المعرفة بعد.',remove:'حذف',retry:'أعد الفهرسة',
   privacyNote:'يمكن فقط للمستخدمين المصرح لهم في حسابك البحث في هذه البيانات.',
   error:'تعذر إكمال الطلب. حاول مرة أخرى.',statusProcessing:'قيد الفهرسة',statusQueued:'في الانتظار',statusFailed:'يتطلب المراجعة',
 }
} as const;

export function extractSearchHits(payload: unknown): SearchHit[] {
  if (!payload || typeof payload !== 'object') return [];
  const raw=payload as Record<string,unknown>;
  const hits=Array.isArray(raw.chunks)?raw.chunks:Array.isArray(raw.results)?raw.results:[];
  return hits.filter((h):h is Record<string,unknown>=>Boolean(h&&typeof h==='object'&&!Array.isArray(h)))
    .filter(h=>typeof h.text==='string'&&h.text.trim().length>0)
    .slice(0,8)
    .map(h=>{
      const item=h.item&&typeof h.item==='object'&&!Array.isArray(h.item)?h.item as Record<string,unknown>:null;
      const key=typeof item?.key==='string'?item.key:typeof h.key==='string'?h.key:undefined;
      return {
      text:String(h.text),
      ...(typeof h.id==='string'?{id:h.id}:{}),
      ...(key!==undefined?{key}:{}),
      ...(typeof h.title==='string'?{title:h.title}:{}),
      ...(typeof h.score==='number'?{score:h.score}:{}),
      ...(typeof h.source_id==='string'?{source_id:h.source_id}:{}),
      };
    });
}
export function AutoRagPage() {
  const ar=typeof document!=='undefined' && document.documentElement.lang.toLowerCase().startsWith('ar');
  const t=ar?wording.ar:wording.en;
  const [status,setStatus]=useState<Status|null>(null);
  const [documents,setDocuments]=useState<DocumentRow[]>([]);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const [question,setQuestion]=useState('');
  const [searched,setSearched]=useState(false);
  const [hits,setHits]=useState<SearchHit[]>([]);
  const [title,setTitle]=useState('');
  const [category,setCategory]=useState('onboarding');
  const [content,setContent]=useState('');
  const [approve,setApprove]=useState(false);
  const [safe,setSafe]=useState(false);
  async function refresh(){
    const [s,docs]=await Promise.all([
      bff<Status>('/v1/auto-rag/status'),
      bff<DocumentRow[]>('/v1/auto-rag/documents'),
    ]);
    setStatus(s);setDocuments(docs);
  }
  useEffect(()=>{
    let live=true;
    Promise.all([bff<Status>('/v1/auto-rag/status'),bff<DocumentRow[]>('/v1/auto-rag/documents')])
      .then(([s,docs])=>{if(live){setStatus(s);setDocuments(docs);}})
      .catch(()=>{if(live)setError(t.error);});
    return()=>{live=false;};
  },[]);
  async function action(op:()=>Promise<void>) {
    setBusy(true);setError('');
    try{await op();await refresh();}catch(e){setError(e instanceof Error?e.message:t.error);}finally{setBusy(false);}
  }
  function search(e:FormEvent) {
    e.preventDefault();
    void action(async()=>{
      const result=await bff<unknown>(`/v1/auto-rag/search?q=${encodeURIComponent(question)}`);
      setHits(extractSearchHits(result));
      setSearched(true);
    });
  }
  function submit(e:FormEvent) {
    e.preventDefault();
    void action(async()=>{
      await bffJson('/v1/auto-rag/documents','POST',{
        title,category,content,approvedForIndexing:approve,containsPersonalOrHealthData:!safe,
      });
      setTitle('');setContent('');setApprove(false);setSafe(false);
    });
  }
  const remove=(id:string)=>void action(async()=>{await bffJson(`/v1/auto-rag/documents/${encodeURIComponent(id)}` as const,'DELETE');});
  const retry=(id:string)=>void action(async()=>{await bffJson(`/v1/auto-rag/documents/${encodeURIComponent(id)}/retry` as const,'POST',{});});
  return <section className="qm-page qm-rag" aria-labelledby="auto-rag-heading" dir={ar?'rtl':'ltr'}>
    <header className="qm-rag__hero">
      <div>
        <div className="qm-rag__eyebrow">{t.eyebrow}</div>
        <h1 id="auto-rag-heading">{t.title}</h1>
        <p>{t.subtitle}</p>
      </div>
      <div className="qm-rag__actions">
        <span className="qm-rag__status"><span className="qm-rag__dot" aria-hidden="true"/>{status?.available?t.connected:t.unavailable}</span>
        <Button disabled={busy||!status?.available} onClick={()=>void action(async()=>{await bffJson('/v1/auto-rag/setup','POST',{});})}>{t.enable}</Button>
        <Button disabled={busy} onClick={()=>void action(async()=>{await refresh();})}>{t.refresh}</Button>
      </div>
    </header>
    {error&&<div className="qm-rag__alert" role="alert">{error}</div>}
    <div className="qm-rag__grid">
      <div className="qm-rag__panel">
        <h2>{t.searchTitle}</h2>
        <p className="qm-rag__description">{t.searchHint}</p>
        <form onSubmit={search} className="qm-rag__queryline">
          <input className="qm-rag__query" aria-label={t.searchTitle} placeholder={t.searchPlaceholder} minLength={2} maxLength={500} required value={question} onChange={e=>setQuestion(e.target.value)}/>
          <Button type="submit" variant="primary" disabled={busy||question.trim().length<2}>{busy?t.searching:t.searchButton}</Button>
        </form>
        {searched&&(hits.length===0?<p className="qm-rag__empty" role="status">{t.noResults}</p>:<ol className="qm-rag__hits" aria-label={t.searchTitle}>{hits.map((hit,i)=><li className="qm-rag__hit" key={hit.id||hit.key||i}>
          <div className="qm-rag__hithead"><strong>{hit.title||hit.key||`${t.passage} ${i+1}`}</strong>{typeof hit.score==='number'&&<span className="qm-rag__meta">{t.relevance}: {Math.round(hit.score*100)}%</span>}</div>
          <p className="qm-rag__hittext">{hit.text}</p>
        </li>)}</ol>)}
      </div>
      <div className="qm-rag__panel">
        <h2>{t.addTitle}</h2>
        <p className="qm-rag__description">{t.addHint}</p>
        <form className="qm-rag__form" onSubmit={submit}>
          <label className="qm-rag__field">{t.name}<input required maxLength={160} value={title} onChange={e=>setTitle(e.target.value)}/></label>
          <label className="qm-rag__field">{t.category}<select value={category} onChange={e=>setCategory(e.target.value)}>{CATEGORIES.map(c=><option key={c} value={c}>{c.replaceAll('_',' ')}</option>)}</select></label>
          <label className="qm-rag__field">{t.details}<textarea required maxLength={90000} value={content} onChange={e=>setContent(e.target.value)}/></label>
          <label className="qm-rag__consent"><input type="checkbox" checked={approve} onChange={e=>setApprove(e.target.checked)}/>{t.consent}</label>
          <label className="qm-rag__consent"><input type="checkbox" checked={safe} onChange={e=>setSafe(e.target.checked)}/>{t.privacy}</label>
          <Button type="submit" variant="primary" disabled={busy||!approve||!safe||!title.trim()||!content.trim()}>{t.save}</Button>
        </form>
      </div>
    </div>
    <div className="qm-rag__panel">
      <h2>{t.documents}</h2>
      <p className="qm-rag__description">{t.documentHint}</p>
      {documents.length===0?<div className="qm-rag__empty">{t.empty}</div>:<ul className="qm-rag__documents">
        {documents.map(d=><li className="qm-rag__document" key={d.id}>
          <div><strong>{d.title}</strong><div className="qm-rag__meta">{d.category.replaceAll('_',' ')} · {d.status==='failed'?t.statusFailed:d.status==='queued'?t.statusQueued:d.status==='processing'?t.statusProcessing:d.status} · {new Date(d.created_at).toLocaleDateString(ar?'ar-AE':'en-GB')}</div></div>
          <div className="qm-rag__footer">{d.status==='failed'&&<Button disabled={busy} onClick={()=>retry(d.id)}>{t.retry}</Button>}<Button disabled={busy} onClick={()=>remove(d.id)}>{t.remove}</Button></div>
        </li>)}
      </ul>}
      <p className="qm-rag__notice">{t.privacyNote}</p>
    </div>
  </section>;
}

